import {useState, useEffect, useRef} from 'react';
import apiClient from '../../context/apiClient';
import ClockIcon from '../../assets/icons/Clock.svg';
import PinIcon from '../../assets/icons/Map_pin.svg';
import CoffeeBG from '../../assets/icons/CoffeeBG.svg';
import UserBG from '../../assets/icons/UserBG.svg';
import UsersBG from '../../assets/icons/UsersBG.svg';
import ToolBG from '../../assets/icons/ToolBG.svg';
import Check from '../../assets/icons/Check.svg';


const languageFlags = {
    DE: "🇩🇪", EN: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
    NE: "🇳🇱", PL: "🇵🇱", TR: "🇹🇷", PT: "🇵🇹", RU: "🇷🇺",
    AR: "🇸🇦", ZH: "🇨🇳", JA: "🇯🇵", KO: "🇰🇷", SE: "🇸🇪",
    NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", HU: "🇭🇺", CZ: "🇨🇿",
    SK: "🇸🇰", RO: "🇷🇴", BG: "🇧🇬", HR: "🇭🇷", EL: "🇬🇷",
};

const parseLocationString = (locationString) => {
    if (!locationString || typeof locationString !== 'string') return null;

    const result = {};
    const regex = /(\w+)=([^,)]*)/g;
    let match;
    while ((match = regex.exec(locationString)) !== null) {
        const key = match[1];
        const value = match[2].trim();
        result[key] = value;
    }

    return result;
};

const EventImportCard = ({event, allEmployees, onEventSaved}) => {
    const {
        name,
        timeframe,
        numberLeader,
        numberInstructors,
        numberMechanics,
        numberGuestManagers
    } = event;

    const location = typeof event.location === 'string'
        ? parseLocationString(event.location)
        : event.location;

    const languages = event.languages ?? [];
    const qualifications = event.qualifications ?? [];

    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [allQualifications, setAllQualifications] = useState([]);
    const [eventQualifications, setEventQualifications] = useState(qualifications ?? []);
    const [qualificationInput, setQualificationInput] = useState('');
    const [newQualificationSuffix, setNewQualificationSuffix] = useState('');
    const [eventLanguages, setEventLanguages] = useState(languages ?? []);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [confirmingSave, setConfirmingSave] = useState(false);


    const instructorOptions = [];
    const mechanicOptions = [];
    const guestManagerOptions = [];

    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [assignments, setAssignments] = useState({
        leader: [], // Kursleiter
        instructor: [],
        mechanic: [],
        guestManager: [],
    });


    const languageDropdownRef = useRef();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                languageDropdownRef.current &&
                !languageDropdownRef.current.contains(event.target)
            ) {
                setShowLanguageDropdown(false);
            }
        };

        if (showLanguageDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLanguageDropdown]);

    useEffect(() => {
        const leaderId = event.leader?.id || null;
        const employees = event.employeeList || [];

        const groupedAssignments = {
            leader: leaderId ? [leaderId] : [],
            instructor: [],
            mechanic: [],
            guestManager: [],
        };

        employees.forEach(emp => {
            switch (emp.role) {
                case 'INSTRUCTOR':
                    groupedAssignments.instructor.push(emp.id);
                    break;
                case 'MECHANIC':
                    groupedAssignments.mechanic.push(emp.id);
                    break;
                case 'GUEST_MANAGER':
                    groupedAssignments.guestManager.push(emp.id);
                    break;
                default:
                    break;
            }
        });

        setAssignments(groupedAssignments);
    }, [event]);
    const getQualiLabel = (name) => {
        const match = name.match(/^(.*?)(?:_(KL|M|GM))?$/);
        const base = match?.[1] || name;
        const suffix = match?.[2];
        if (!suffix) return base;

        const suffixMap = {
            KL: ' (KL)',
            M: ' (M)',
            GM: ' (GM)',
        };

        return `${base}${suffixMap[suffix] || ''}`;
    };

    const formatLocation = (locationInput) => {
        const loc = typeof locationInput === 'string'
            ? parseLocationString(locationInput)
            : locationInput;

        if (!loc) return "";

        const {street, streetNumber, postalCode, city, country} = loc;
        return [
            street && streetNumber ? `${street} ${streetNumber}` : null,
            postalCode || city ? `${postalCode ?? ""} ${city ?? ""}`.trim() : null,
            country,
        ].filter(Boolean).join(", ");
    };

    const handleSaveEvent = async () => {
        try {
            const leader = assignments.leader[0]
                ? buildEmployeeDTO(assignments.leader[0])
                : null;

            const employeeList = Object.entries(assignments)
                .flatMap(([role, ids]) => {
                    if (role === "leader") return [];
                    return ids.map(id => buildEmployeeDTO(id));
                })
                .filter(Boolean);

            const payload = {
                ...event,
                location: formatLocation(event.location),
                languages: eventLanguages,
                qualifications: eventQualifications.map(q => ({name: q})),
                leader: leader,
                employeeList: employeeList,
                numberLeader: bedarf.numberLeader,
                numberInstructors: bedarf.numberInstructors,
                numberMechanics: bedarf.numberMechanics,
                numberGuestManagers: bedarf.numberGuestManagers,
                assignments,
            };
            console.log("Payload:", payload);

            const response = await apiClient.post('/api/events/addEvent', payload, {
                withCredentials: true,
            });

            if (response?.status === 200) {
                if (onEventSaved) {
                    onEventSaved(event.id);
                }
            } else {
                alert("Speichern fehlgeschlagen ❌");
            }
        } catch (error) {
            console.error("Fehler beim Speichern des Events:", error);
            alert("Fehler beim Speichern des Events ❌");
        }
    };


    useEffect(() => {
        const fetchQualifications = async () => {
            try {
                const res = await apiClient.get('/api/utility/allQualifications');
                if (res.status === 200) {
                    const names = res.data.map(q => q.name);
                    setAllQualifications(names);
                }
            } catch (err) {
                console.error("Fehler beim Laden der Qualifikationen", err);
            }
        };
        fetchQualifications();
    }, []);

    const handleAddLanguage = (langCode) => {
        if (!eventLanguages.includes(langCode)) {
            setEventLanguages(prev => [...prev, langCode]);
        }
    };

    const handleRemoveLanguage = (langCode) => {
        setEventLanguages(prev => prev.filter(l => l !== langCode));
    };

    const [bedarf, setBedarf] = useState({
        numberLeader: event.numberLeader,
        numberInstructors: event.numberInstructors,
        numberMechanics: event.numberMechanics,
        numberGuestManagers: event.numberGuestManagers
    });

    useEffect(() => {
        setBedarf(prev => {
            const updated = {...prev};

            const roleKeyToBedarfKey = {
                leader: 'numberLeader',
                instructor: 'numberInstructors',
                mechanic: 'numberMechanics',
                guestManager: 'numberGuestManagers',
            };

            Object.entries(assignments).forEach(([role, ids]) => {
                const bedarfKey = roleKeyToBedarfKey[role];
                if (bedarfKey) {
                    const assignedCount = ids.length;
                    if (assignedCount > prev[bedarfKey]) {
                        updated[bedarfKey] = assignedCount;
                    }
                }
            });

            return updated;
        });
    }, [assignments]);


    const handleChange = (key, delta) => {
        setBedarf(prev => ({
            ...prev,
            [key]: Math.max(0, (prev[key] || 0) + delta)
        }));
    };

    const getMinValueForRole = (key) => {
        const roleMap = {
            numberLeader: 'leader',
            numberInstructors: 'instructor',
            numberMechanics: 'mechanic',
            numberGuestManagers: 'guestManager',
        };

        const roleKey = roleMap[key];
        return assignments[roleKey]?.length || 0;
    };


    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });

    const formatAddress = () => {
        return `${location?.country || ''}, ${location?.city || ''}`;
    };


    const uniqueQualifications = (() => {
        const uniqueBaseNames = new Map();
        const priority = {KL: 3, M: 2, GM: 1, DEFAULT: 0};

        (qualifications || []).forEach((q) => {
            const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
            const base = match?.[1] || q;
            const suffix = match?.[2] || "DEFAULT";

            const current = uniqueBaseNames.get(base);
            if (!current || priority[suffix] > priority[current.suffix]) {
                uniqueBaseNames.set(base, {base, suffix});
            }
        });

        return Array.from(uniqueBaseNames.values());
    })();

    const colorMap = {
        KL: "bg-indigo-500 text-white",
        DEFAULT: "bg-indigo-100 text-indigo-800",
        M: "bg-emerald-200 text-emerald-900",
        GM: "bg-rose-200 text-rose-900",
    };

    allQualifications.forEach((q) => {
        const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
        const base = match?.[1] || q;
        const suffix = match?.[2];

        const label = getQualiLabel(q);

        if (!suffix || suffix === "KL") {
            instructorOptions.push({label, value: q});
        } else if (suffix === "M") {
            mechanicOptions.push({label, value: q});
        } else if (suffix === "GM") {
            guestManagerOptions.push({label, value: q});
        }
    });

    instructorOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    mechanicOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    guestManagerOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));


    const sortQualifications = (arr) => {
        const suffixOrder = {KL: 0, M: 1, GM: 2, DEFAULT: 3};

        return [...arr].sort((a, b) => {
            const matchA = a.match(/^(.*?)(?:_(KL|M|GM))?$/);
            const matchB = b.match(/^(.*?)(?:_(KL|M|GM))?$/);

            const baseA = matchA?.[1] || a;
            const baseB = matchB?.[1] || b;
            const suffixA = matchA?.[2] || "DEFAULT";
            const suffixB = matchB?.[2] || "DEFAULT";

            const groupDiff = suffixOrder[suffixA] - suffixOrder[suffixB];
            if (groupDiff !== 0) return groupDiff;

            return baseA.localeCompare(baseB, 'de');
        });
    };

    const buildEmployeeDTO = (id) => {
        const emp = allEmployees.find(e => e.id === id);
        return emp
            ? {
                id: emp.id,
                firstName: emp.firstName,
                lastName: emp.lastName,
                location: emp.location ?? "",
                role: emp.role,
                monthlyHours: emp.monthlyHours ?? 0,
                overtime: emp.overtime ?? 0,
                vacation: emp.vacation ?? 0,
                blockedTime: emp.blockedTime ?? [],
                reisepaketePerMonth: emp.reisepaketePerMonth ?? 0,
                languages: emp.languages ?? [],
                qualifications: emp.qualifications ?? [],
                reisepakete: emp.reisepakete ?? 0,
            }
            : null;
    };


    return (
        <div className="relative bg-white shadow-md rounded-2xl p-4 w-full h-[630px] flex flex-col space-y-4">
            {/* Header */}
            <div className="space-y-1 relative h-[110px] mb-6">
                <h2 className="text-lg font-semibold text-gray-800 pr-10">
                    {name || 'Unbenanntes Event'}
                </h2>

                <div className="absolute top-0 right-0 flex gap-2 items-center justify-end">
                    {confirmingSave ? (
                        <>
                            <button
                                onClick={() => setConfirmingSave(false)}
                                title="Abbrechen"
                                className="w-8 h-8 rounded-full bg-red-200 hover:bg-red-300 text-red-700 flex items-center justify-center shadow-md transition"
                            >
                                ✕
                            </button>
                            <button
                                onClick={handleSaveEvent}
                                title="Speichern bestätigen"
                                className="w-8 h-8 rounded-full bg-indigo-400 hover:bg-indigo-300 text-white flex items-center justify-center shadow-md transition"
                            >
                                <img src={Check} alt="Speichern" className="h-4 w-4"/>
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => setConfirmingSave(true)}
                            title="Event speichern"
                            className="w-8 h-8 rounded-full bg-indigo-400 hover:bg-indigo-300 text-white flex items-center justify-center shadow-md transition"
                        >
                            <img src={Check} alt="Speichern" className="h-4 w-4"/>
                        </button>
                    )}
                </div>


                <div className="flex items-center text-sm text-gray-600 gap-2">
                    <img src={PinIcon} alt="Ort" className="h-4 w-4"/>
                    <span>{formatAddress()}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 gap-2">
                    <img src={ClockIcon} alt="Uhrzeit" className="h-4 w-4"/>
                    <span>{formatDate(timeframe.startTime)} - {formatDate(timeframe.endTime)}</span>
                </div>
            </div>


            {/* Requirements */}
            <div className="flex-1 min-h-[110px] px-1 relative overflow-visible z-10">
                <h3 className="text-sm font-medium text-gray-500 text-center border-b pb-1 mb-2">
                    Anforderungen
                </h3>
                {eventQualifications.length > 0 ? (
                    <div
                        onClick={() => setShowQualificationModal(true)}
                        className="flex flex-wrap justify-center gap-2 cursor-pointer group"
                    >
                        {(() => {
                            const colorMap = {
                                KL: "bg-indigo-500 text-white",
                                DEFAULT: "bg-indigo-100 text-indigo-800",
                                M: "bg-emerald-200 text-emerald-900",
                                GM: "bg-rose-200 text-rose-900",
                            };
                            const suffixOrder = { KL: 0, DEFAULT: 1, M: 2, GM: 3 };

                            const items = (eventQualifications || []).map((q) => {
                                const m = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                return { base: m?.[1] || q, suffix: m?.[2] || "DEFAULT", raw: q };
                            });

                            items.sort((a, b) => {
                                const g = suffixOrder[a.suffix] - suffixOrder[b.suffix];
                                return g !== 0 ? g : a.base.localeCompare(b.base, "de");
                            });

                            return items.map(({ base, suffix, raw }, idx) => (
                                <span
                                    key={`req-${idx}-${raw}`}
                                    className={`text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT}`}
                                    title={raw}
                                >
      {base}
    </span>
                            ));
                        })()}

                    </div>
                ) : (
                    // ✅ empty state still here
                    <div
                        onClick={() => setShowQualificationModal(true)}
                        className="flex justify-center items-center h-[32px] cursor-pointer text-sm text-gray-400 hover:text-indigo-600 transition"
                    >
                        + Qualifikation hinzufügen
                    </div>
                )}

                {/* Sprachen (unter Qualifikationen) */}
                <div className="mt-2 flex flex-wrap justify-center gap-2 items-center">
                    {eventLanguages.map((lang) => (
                        <button
                            key={lang}
                            onClick={() => handleRemoveLanguage(lang)}
                            className="flex items-center space-x-1 bg-indigo-100 text-indigo-800 text-sm px-2 py-1 rounded-full hover:bg-red-100 hover:text-red-600 transition"
                            title="Klicken zum Entfernen"
                        >
                            <span>{languageFlags[lang] || lang}</span>
                            <span className="text-xs font-bold">&times;</span>
                        </button>
                    ))}

                    {/* Plus Button */}
                    <div ref={languageDropdownRef} className="relative z-50">
                        <button
                            onClick={() => setShowLanguageDropdown(prev => !prev)}
                            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition text-sm font-bold leading-none"
                            title="Sprache hinzufügen"
                        >
                            +
                        </button>


                        {showLanguageDropdown && (
                            <div
                                className="absolute left-0 mt-2 z-50 bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-y-auto w-32">
                                {Object.keys(languageFlags)
                                    .filter((lang) => !eventLanguages.includes(lang))
                                    .map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => {
                                                handleAddLanguage(lang);
                                                setShowLanguageDropdown(false);
                                            }}
                                            className="w-full text-left text-xl px-2 py-1 hover:bg-indigo-100 hover:text-indigo-800 rounded transition"
                                        >
                                            {languageFlags[lang]}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Direktzuweisungen */}
            <div className="flex-1 min-h-[110px] overflow-y-auto px-1">
                <h3 className="text-sm font-medium text-gray-500 text-center border-b pb-1 mb-2">
                    Direktzuweisungen
                </h3>

                {Object.values(assignments).every(arr => arr.length === 0) ? (
                    <div className="flex justify-center">
                        <button
                            onClick={() => setShowAssignmentModal(true)}
                            className="text-sm text-gray-400 hover:text-indigo-600 transition"
                        >
                            + Direktzuweisungen hinzufügen
                        </button>
                    </div>
                ) : (
                    <div
                        onClick={() => setShowAssignmentModal(true)}
                        className="flex flex-wrap justify-center gap-2 cursor-pointer group"
                    >
                        {Object.entries(assignments).flatMap(([role, ids]) =>
                            ids.map((id) => {
                                const emp = allEmployees.find(e => e.id === id);
                                if (!emp) return null;

                                const roleMap = {
                                    leader: "bg-indigo-500 text-white",
                                    instructor: "bg-indigo-100 text-indigo-800",
                                    mechanic: "bg-emerald-200 text-emerald-900",
                                    guestManager: "bg-rose-200 text-rose-900",
                                };

                                return (
                                    <span
                                        key={`${role}-${id}`}
                                        className={`text-xs px-3 py-1 rounded-full ${roleMap[role]}`}
                                    >
                                            {emp.firstName} {emp.lastName}
                                        </span>
                                );
                            })
                        )}
                    </div>
                )}
            </div>


            <div className="relative group w-full h-40 grid grid-cols-2 grid-rows-2 text-center text-base font-medium">
                {/* Grid-Inhalte */}
                {[
                    {value: bedarf.numberLeader, key: 'numberLeader', bg: UserBG},
                    {value: bedarf.numberInstructors, key: 'numberInstructors', bg: UsersBG},
                    {value: bedarf.numberMechanics, key: 'numberMechanics', bg: ToolBG},
                    {value: bedarf.numberGuestManagers, key: 'numberGuestManagers', bg: CoffeeBG},
                ].map((item, idx) => (
                    <div
                        key={idx}
                        className="relative flex items-center justify-center bg-white"
                        style={{
                            backgroundImage: `url(${item.bg})`,
                            backgroundSize: '50%',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                        }}
                    >
                        <input
                            type="number"
                            min={getMinValueForRole(item.key)}
                            value={bedarf[item.key]}
                            onChange={(e) => {
                                const inputVal = parseInt(e.target.value) || 0;
                                const minVal = getMinValueForRole(item.key);
                                setBedarf(prev => ({
                                    ...prev,
                                    [item.key]: Math.max(minVal, inputVal),
                                }));
                            }}
                            className="w-full h-full text-center text-4xl font-light text-black bg-transparent appearance-none focus:outline-none hover:appearance-auto transition-all"
                            style={{
                                MozAppearance: 'textfield',
                            }}
                        />
                    </div>
                ))}

                {/* Linien */}
                <div className="absolute top-1/2 left-0 w-full h-px bg-gray-300 pointer-events-none"/>
                <div className="absolute top-0 left-1/2 h-full w-px bg-gray-300 pointer-events-none"/>
            </div>


            {showQualificationModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                    <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Qualifikationen bearbeiten</h2>

                        <div className="flex flex-wrap gap-2 mb-4 max-h-[150px] overflow-y-auto pr-1">
                            {(() => {
                                const colorMap = {
                                    KL: "bg-indigo-500 text-white",
                                    DEFAULT: "bg-indigo-100 text-indigo-800",
                                    M: "bg-emerald-200 text-emerald-900",
                                    GM: "bg-rose-200 text-rose-900",
                                };
                                const suffixOrder = { KL: 0, DEFAULT: 1, M: 2, GM: 3 };

                                const items = (eventQualifications || []).map((q) => {
                                    const m = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                    return { base: m?.[1] || q, suffix: m?.[2] || "DEFAULT", raw: q };
                                });

                                items.sort((a, b) => {
                                    const g = suffixOrder[a.suffix] - suffixOrder[b.suffix];
                                    return g !== 0 ? g : a.base.localeCompare(b.base, "de");
                                });

                                return items.map(({ base, suffix, raw }, idx) => (
                                    <button
                                        key={`modal-${idx}-${raw}`}
                                        onClick={() => setEventQualifications((prev) => prev.filter((x) => x !== raw))}
                                        className={`text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT} hover:bg-red-100 hover:text-red-600`}
                                        title="Klicken zum Entfernen"
                                    >
                                        {base}
                                    </button>
                                ));
                            })()}

                        </div>


                        {/* Dropdown zum Hinzufügen */}
                        <div className="flex flex-col gap-2">
                            <select
                                multiple
                                size={20}
                                onChange={(e) => {
                                    const selected = e.target.value;
                                    if (selected && !eventQualifications.includes(selected)) {
                                        setEventQualifications(prev => [...prev, selected]);
                                    }
                                    // e.target.value = "";
                                }}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            >
                                {instructorOptions.length > 0 && (
                                    <optgroup label="Instruktoren">
                                        {instructorOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}

                                {mechanicOptions.length > 0 && (
                                    <optgroup label="Mechaniker">
                                        {mechanicOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}

                                {guestManagerOptions.length > 0 && (
                                    <optgroup label="Gästebetreuer">
                                        {guestManagerOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowQualificationModal(false)}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showAssignmentModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                    <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Direktzuweisungen bearbeiten</h2>

                        {["leader", "instructor", "mechanic", "guestManager"].map((roleKey) => {
                            const roleLabels = {
                                leader: "Kursleiter",
                                instructor: "Instruktoren",
                                mechanic: "Mechaniker",
                                guestManager: "Gästebetreuer",
                            };

                            const roleColor = {
                                leader: "bg-indigo-500 text-white",
                                instructor: "bg-indigo-100 text-indigo-800",
                                mechanic: "bg-emerald-200 text-emerald-900",
                                guestManager: "bg-rose-200 text-rose-900",
                            };

                            return (
                                <div key={roleKey} className="mb-4">
                                    <label className="block text-sm text-gray-600 mb-1">{roleLabels[roleKey]}</label>

                                    <div className="flex flex-wrap gap-2 mb-2 max-h-[80px] overflow-y-auto pr-1">
                                        {assignments[roleKey].map((id, idx) => {
                                            const emp = allEmployees.find(e => e.id === id);
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() =>
                                                        setAssignments(prev => ({
                                                            ...prev,
                                                            [roleKey]: prev[roleKey].filter(x => x !== id),
                                                        }))
                                                    }
                                                    className={`text-xs px-3 py-1 rounded-full ${roleColor[roleKey]} hover:bg-red-100 hover:text-red-600`}
                                                >
                                                    {emp.firstName} {emp.lastName}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <select
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            if (id && !assignments[roleKey].includes(id)) {
                                                setAssignments(prev => ({
                                                    ...prev,
                                                    [roleKey]: [...prev[roleKey], id],
                                                }));
                                            }
                                            e.target.value = '';
                                        }}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="">+ Person hinzufügen</option>
                                        {allEmployees
                                            .filter(emp => {
                                                const alreadyAssigned = Object.values(assignments).flat().includes(emp.id);
                                                const roleMap = {
                                                    leader: 'INSTRUCTOR',
                                                    instructor: 'INSTRUCTOR',
                                                    mechanic: 'MECHANIC',
                                                    guestManager: 'GUEST_MANAGER',
                                                };
                                                return !alreadyAssigned && emp.role === roleMap[roleKey];
                                            })
                                            .sort((a, b) =>
                                                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'de')
                                            )
                                            .map(emp => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.firstName} {emp.lastName}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            );
                        })}

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowAssignmentModal(false)}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventImportCard;
